/**
 * `CategoryFilter` — segmented chip bar that controls which category
 * of metrics sections render in `MetricsView`. Single-select (All or
 * one category). Persists to `localStorage` via the caller.
 *
 * Categories mirror the section grouping in MetricsView:
 *   - all          → every section
 *   - growth       → new accounts / total / sites / transactional sites
 *   - engagement   → activity volume, engagement intensity / tenure
 *   - retention    → cohort heatmap + churn + churn-risk + dormant
 *   - revenue      → live settlements + volume by currency
 *   - governance   → mandates, approvals, escalations
 *   - geography    → country breakdown
 *
 * Visual: mono uppercase chips in the admin's idiom, lime accent on
 * the active chip. Wraps gracefully on narrow viewports; tap targets
 * are ≥40px tall to meet the mobile a11y minimum.
 */

import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";

export type MetricsCategory =
  | 'all'
  | 'growth'
  | 'engagement'
  | 'retention'
  | 'revenue'
  | 'governance'
  | 'geography'
  | 'assistants';

export const METRICS_CATEGORIES: ReadonlyArray<{
  value: MetricsCategory;
  label: string;
}> = [
  { value: 'all', label: 'All' },
  { value: 'growth', label: 'Growth' },
  { value: 'engagement', label: 'Engagement' },
  { value: 'retention', label: 'Retention & churn' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'governance', label: 'Governance' },
  { value: 'geography', label: 'Geography' },
  { value: 'assistants', label: 'Assistants' },
];

const VALID: ReadonlySet<MetricsCategory> = new Set(
  METRICS_CATEGORIES.map((c) => c.value),
);

export function isMetricsCategory(v: unknown): v is MetricsCategory {
  return typeof v === 'string' && VALID.has(v as MetricsCategory);
}

export interface CategoryFilterProps {
  value: MetricsCategory;
  onChange: (next: MetricsCategory) => void;
  disabled?: boolean;
}

export function CategoryFilter({
  value,
  onChange,
  disabled,
}: CategoryFilterProps): JSX.Element {
  const C = useC();
  return (
    <div
      role="group"
      aria-label="Metric category"
      className="flex flex-wrap gap-1.5"
    >
      {METRICS_CATEGORIES.map((cat) => {
        const active = cat.value === value;
        return (
          <button
            key={cat.value}
            type="button"
            onClick={() => {
              if (!active) onChange(cat.value);
            }}
            disabled={disabled}
            aria-pressed={active}
            className="px-3"
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              color: active ? C.limeFg : C.muted,
              background: active ? C.limeDim : C.surface2,
              border: `1px solid ${active ? C.limeFg : C.border}`,
              borderRadius: 2,
              cursor: active || disabled === true ? 'default' : 'pointer',
              opacity: disabled === true ? 0.6 : 1,
              minHeight: 40,
              whiteSpace: 'nowrap',
              fontWeight: 500,
            }}
          >
            {cat.label}
          </button>
        );
      })}
    </div>
  );
}
